import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const CACHE_KEY = "mm-legal-accepted";

export function useLegalAcceptance() {
  const { user } = useAuth();

  // Returning users have this flag in localStorage — skip the DB round-trip.
  const cached = typeof window !== "undefined" && localStorage.getItem(CACHE_KEY) === "1";
  const [accepted, setAccepted] = useState<boolean | null>(cached ? true : null);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!user) {
      setAccepted(null);
      setLoading(false);
      return;
    }

    const check = async () => {
      try {
        const { data } = await supabase
          .from("legal_acceptances")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();
        const result = !!data;
        setAccepted(result);
        if (result) localStorage.setItem(CACHE_KEY, "1");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[useLegalAcceptance] check failed:", e);
        // Fail open — if the DB is unreachable don't block the user.
        setAccepted(true);
      } finally {
        setLoading(false);
      }
    };

    check();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async (_dob?: Date): Promise<{ error: Error | null }> => {
    if (!user) {
      return { error: new Error("No authenticated user. Please sign in again.") };
    }

    // Check if already accepted — avoid duplicate insert errors.
    const { data: existing } = await supabase
      .from("legal_acceptances")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (existing) {
      setAccepted(true);
      return { error: null };
    }

    const { error } = await supabase
      .from("legal_acceptances")
      .insert({
        user_id: user.id,
        accepted_at: new Date().toISOString(),
        terms_version: "1.0",
      });

    if (error) {
      // eslint-disable-next-line no-console
      console.error("[useLegalAcceptance] insert failed:", error);
      return { error: new Error(error.message || "Failed to save acceptance") };
    }
    setAccepted(true);
    return { error: null };
  };

  return { accepted, loading, accept };
}
